/**
 * AskArloBanner.js — logged-out promo above the featured carousel / landing hero.
 *
 * Most people never discover Ask Arlo because it only lights up once you're
 * signed in. This banner is the invitation: a friend with great taste waiting
 * in the dark. The CTA is create-account / log-in.
 *
 * Design: "the glow of a screen in a dark room." The Arlo constellation is a
 * living light source — a teal aurora breathes behind it, its nodes twinkle.
 * Editorial serif headline, mono eyebrow, staggered reveal. All ambient motion
 * respects prefers-reduced-motion.
 *
 * Self-gating: renders nothing for authenticated users, and stays dismissed
 * (localStorage) once closed so it never nags. Copy follows Arlo's voice —
 * warm, sentence case, second person, never "AI".
 */
import React, { useState, useCallback } from 'react';
import { useStreamAuth } from '../../hooks/useStreamAuth';
import './AskArloBanner.css';

const DISMISS_KEY = 'chillfree_arlo_banner_dismissed';

// Arlo "A" constellation, recolored to the ChillFree teal family. Inlined
// (not the flat asset) so the nodes can twinkle.
const ArloConstellation = () => (
  <svg
    className="arlo-banner__glyph"
    viewBox="0 0 120 120"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <defs>
      <radialGradient id="arloBannerGlow" cx="0.5" cy="0.5" r="0.5">
        <stop offset="0%" stopColor="#7fe0d2" stopOpacity="0.65" />
        <stop offset="55%" stopColor="#56bdaf" stopOpacity="0.18" />
        <stop offset="100%" stopColor="#56bdaf" stopOpacity="0" />
      </radialGradient>
      <linearGradient id="arloBannerCore" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#d6fbf4" />
        <stop offset="55%" stopColor="#7fe0d2" />
        <stop offset="100%" stopColor="#56bdaf" />
      </linearGradient>
    </defs>
    <g stroke="#7fe0d2" strokeOpacity="0.55" strokeWidth="2" strokeLinecap="round">
      <line x1="60" y1="20" x2="39" y2="60" />
      <line x1="39" y1="60" x2="22" y2="100" />
      <line x1="60" y1="20" x2="81" y2="60" />
      <line x1="81" y1="60" x2="98" y2="100" />
      <line x1="39" y1="60" x2="81" y2="60" />
    </g>
    <circle className="arlo-banner__node arlo-banner__node--1" cx="39" cy="60" r="5" fill="#eafffb" />
    <circle className="arlo-banner__node arlo-banner__node--2" cx="81" cy="60" r="5" fill="#eafffb" />
    <circle className="arlo-banner__node arlo-banner__node--3" cx="22" cy="100" r="5.5" fill="#eafffb" />
    <circle className="arlo-banner__node arlo-banner__node--4" cx="98" cy="100" r="5.5" fill="#eafffb" />
    <circle cx="60" cy="20" r="30" fill="url(#arloBannerGlow)" />
    <circle cx="60" cy="20" r="11" fill="url(#arloBannerCore)" />
    <circle cx="60" cy="20" r="11" fill="none" stroke="#eafffb" strokeOpacity="0.5" strokeWidth="0.7" />
  </svg>
);

/**
 * @param {boolean} overlay  When true, the banner floats over the top of the
 *   hero (absolute, zero layout height) instead of sitting in normal flow —
 *   so it never pushes the featured carousel down the fold. The parent must
 *   provide a `position: relative` wrapper around the hero.
 */
const AskArloBanner = ({ overlay = false }) => {
  const { isAuthenticated, loginWithRedirect } = useStreamAuth();

  const [dismissed, setDismissed] = useState(() => {
    try {
      return window.localStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  const handleDismiss = useCallback(() => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private mode — dismiss for this session only */
    }
  }, []);

  const handleSignup = useCallback(() => {
    loginWithRedirect({
      authorizationParams: { screen_hint: 'signup' },
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  }, [loginWithRedirect]);

  const handleLogin = useCallback(() => {
    loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  }, [loginWithRedirect]);

  // Only for logged-out visitors, and only until they close it.
  if (isAuthenticated || dismissed) return null;

  return (
    <aside
      className={`arlo-banner${overlay ? ' arlo-banner--overlay' : ''}`}
      aria-label="Meet Arlo"
    >
      <div className="arlo-banner__aurora" aria-hidden="true" />
      <div className="arlo-banner__grain" aria-hidden="true" />

      <div className="arlo-banner__inner">
        <div className="arlo-banner__mark">
          <ArloConstellation />
        </div>

        <div className="arlo-banner__copy">
          <span className="arlo-banner__eyebrow">Powered by Arlo</span>
          <h2 className="arlo-banner__headline">
            Let Arlo pick your <em>night.</em>
          </h2>
          <p className="arlo-banner__sub">
            Create a free account and Arlo learns your taste — then hands you one
            thing worth watching tonight.
          </p>
        </div>

        <div className="arlo-banner__actions">
          <button type="button" className="arlo-banner__cta" onClick={handleSignup}>
            <span>Create account</span>
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" aria-hidden="true">
              <path
                d="M5 12h14M13 6l6 6-6 6"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
          <button type="button" className="arlo-banner__ghost" onClick={handleLogin}>
            Log in
          </button>
        </div>
      </div>

      <button
        type="button"
        className="arlo-banner__close"
        onClick={handleDismiss}
        aria-label="Dismiss"
      >
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
          <path
            d="M18 6 6 18M6 6l12 12"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
          />
        </svg>
      </button>
    </aside>
  );
};

export default AskArloBanner;
